"use client";
import React from 'react';
import { motion } from 'framer-motion';
import { useContactModal } from '../../../../contexts/ContactModalContext';

const IconDiscover = () => (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <circle cx="16" cy="16" r="8" stroke="#080400" strokeWidth="2.8"/>
        <path d="M22 22L30 30" stroke="#080400" strokeWidth="2.8" strokeLinecap="round"/>
        <path d="M13 16h6M16 13v6" stroke="#080400" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
);

const IconDefine = () => (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <rect x="6" y="8" width="24" height="20" rx="4" stroke="#080400" strokeWidth="2.5"/>
        <path d="M11 14h14M11 19h10M11 24h6" stroke="#080400" strokeWidth="2.2" strokeLinecap="round"/>
    </svg>
);

const IconDesign = () => (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <path d="M8 28l5-5M18 6l2 2-9 9-2-2 9-9z" stroke="#080400" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M22 4l10 10-2 2-10-10 2-2z" stroke="#080400" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const IconDeploy = () => (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <path d="M18 6C18 6 10 14 10 20c0 5 3 8 8 8s8-3 8-8c0-6-8-14-8-14z" stroke="#080400" strokeWidth="2.5" strokeLinejoin="round"/>
        <path d="M14 26l-4 4" stroke="#080400" strokeWidth="2.5" strokeLinecap="round"/>
        <circle cx="22" cy="16" r="2.5" fill="#080400"/>
    </svg>
);

const IconGrow = () => (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <path d="M6 28L13 18l7 5L28 8" stroke="#080400" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M22 8h6v6" stroke="#080400" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="28" cy="28" r="4" stroke="#080400" strokeWidth="2.2"/>
        <path d="M26.5 28l1.5 1.5L30 27" stroke="#080400" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const steps = [
    {
        title: 'Discover',
        desc: 'We begin by understanding your business, customers, competitors, and market position. Because successful branding starts with insight, not assumptions.',
        Icon: IconDiscover,
    },
    {
        title: 'Define',
        desc: 'We establish your positioning, messaging architecture, audience insights, and strategic direction — creating a clear and differentiated brand platform.',
        Icon: IconDefine,
    },
    {
        title: 'Design',
        desc: 'We create the visual and verbal identity systems that bring your brand to life — from logo and typography to tone of voice and communication guidelines.',
        Icon: IconDesign,
    },
    {
        title: 'Deploy',
        desc: 'We implement your brand across all customer touchpoints to ensure consistency, clarity, and recognition at every stage of the buyer journey.',
        Icon: IconDeploy,
    },
    {
        title: 'Grow',
        desc: 'As your business evolves, your brand evolves with it — remaining relevant, differentiated, and memorable as you scale into new markets.',
        Icon: IconGrow,
    },
];

const ArrowDown = () => (
    <div className="flex lg:hidden w-full items-center justify-center py-1">
        <svg width="16" height="32" viewBox="0 0 16 32" fill="none">
            <path d="M8 2v20" stroke="#ceff1a" strokeWidth="1.8" strokeLinecap="round"/>
            <path d="M3 18l5 8 5-8" stroke="#ceff1a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    </div>
);

export default function BrandExpectations() {
    const { openModal } = useContactModal();
    return (
        <section className="relative overflow-hidden py-24 px-6 md:px-16 lg:px-24" style={{ background: '#000000' }}>
            <div
                className="absolute inset-0 pointer-events-none opacity-[0.025]"
                style={{
                    backgroundImage: 'radial-gradient(circle, rgba(206,255,26,1) 1px, transparent 1px)',
                    backgroundSize: '40px 40px',
                }}
            />

            <div className="max-w-[1400px] mx-auto flex flex-col gap-20 relative z-10">
                <div className="flex flex-col gap-4 max-w-2xl">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.25em] uppercase"
                        style={{ color: '#ceff1a', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                    >
                        Our Process
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
                        className="font-bold tracking-tight leading-tight"
                        style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontSize: 'clamp(36px, 4vw, 64px)', color: '#fbfdfd' }}
                    >
                        The Lead Workz Branding Process
                    </motion.h2>
                </div>

                <div className="flex flex-col lg:flex-row items-start gap-0">
                    {steps.map((step, i) => (
                        <React.Fragment key={i}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55, delay: i * 0.12 }}
                                className="flex flex-col items-center lg:items-start gap-5 flex-1 min-w-0 w-full lg:w-auto"
                            >
                                <div
                                    className="flex items-center justify-center rounded-full shrink-0"
                                    style={{ width: 88, height: 88, background: '#ceff1a' }}
                                >
                                    <step.Icon />
                                </div>
                                <div className="flex flex-col items-center lg:items-start gap-2 text-center lg:text-left">
                                    <h3
                                        className="font-bold leading-snug"
                                        style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontSize: 'clamp(18px, 1.5vw, 22px)', color: '#fbfdfd', letterSpacing: '0.02em' }}
                                    >
                                        {step.title}
                                    </h3>
                                    <p
                                        className="text-sm leading-relaxed"
                                        style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(251,253,253,0.65)', maxWidth: 260 }}
                                    >
                                        {step.desc}
                                    </p>
                                </div>
                            </motion.div>

                            {i < steps.length - 1 && (
                                <>
                                    <div className="hidden lg:flex items-start justify-center shrink-0 pt-[36px]" style={{ width: 56 }}>
                                        <motion.div
                                            initial={{ scaleX: 0, opacity: 0 }} whileInView={{ scaleX: 1, opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.12 + 0.3 }}
                                            style={{ transformOrigin: 'left' }}
                                        >
                                            <svg width="56" height="16" viewBox="0 0 56 16" fill="none">
                                                <path d="M2 8h40" stroke="#ceff1a" strokeWidth="1.8" strokeLinecap="round"/>
                                                <path d="M38 3l8 5-8 5" stroke="#ceff1a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </motion.div>
                                    </div>
                                    <ArrowDown />
                                </>
                            )}
                        </React.Fragment>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                >
                    <button
                        onClick={openModal}
                        className="inline-flex items-center justify-center px-10 py-5 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
                        style={{ background: '#ceff1a', color: '#080400', fontFamily: 'var(--font-clash), "Clash Display", sans-serif', letterSpacing: '0.08em' }}
                    >
                        Start With A Brand Strategy Session →
                    </button>
                </motion.div>
            </div>
        </section>
    );
}
